import type { Object as PortalObject } from '@gmn-portal/backend';
import type {
  Column,
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Box, Eye, MoreHorizontal, PackageSearch, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCustomer, useDeleteObject, useObjectCategory, useObjects } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import {
  getObjectCategoryIcon,
  normalizeObjectCategoryIconKey,
} from '@/shared/utils/object-category-icons';
import { getSortParam } from '@/shared/utils/table-utils';

interface ObjectsOverviewProps {
  filterForCurrentUserCustomer?: boolean;
}

const ActionCell = ({
  object,
  onDelete,
  onViewDetail,
  filterForCurrentUserCustomer = false,
}: {
  object: PortalObject;
  onDelete: (object: PortalObject) => void;
  onViewDetail: (id: string) => void;
  filterForCurrentUserCustomer?: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = currentUser?.role === 'gmn-admin';

  // Determine the appropriate route based on context
  const editRoute = filterForCurrentUserCustomer
    ? `/c/my-objects/${object.id}`
    : `/gmn/contract-management/objects/${object.id}`;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Container only stops propagation, not interactive
    <div
      className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => onViewDetail(object.id)}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(editRoute)}>
            {filterForCurrentUserCustomer ? t('common.view') : t('common.edit')}
          </DropdownMenuItem>
          {!filterForCurrentUserCustomer && (
            <DropdownMenuItem
              onClick={() => navigate(`/gmn/contract-management/objects/new?copy=${object.id}`)}
              disabled={!isGmnAdmin}
              title={!isGmnAdmin ? t('objects.onlyGmnAdminCanCopy') : undefined}
            >
              {t('objects.copyObject')}
            </DropdownMenuItem>
          )}
          {isGmnAdmin && !filterForCurrentUserCustomer && (
            <DropdownMenuItem onClick={() => onDelete(object)}>
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const CustomerCell = ({
  assignedCustomer,
}: {
  assignedCustomer?: { id: string; name?: string } | string | null;
}) => {
  const customerId = assignedCustomer
    ? typeof assignedCustomer === 'object'
      ? assignedCustomer.id
      : assignedCustomer
    : '';

  const { data: customer, isLoading } = useCustomer(customerId);

  if (!assignedCustomer) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof assignedCustomer === 'object' && assignedCustomer.name) {
    return (
      <span className="text-muted-foreground truncate" title={assignedCustomer.name}>
        {assignedCustomer.name}
      </span>
    );
  }

  if (isLoading) {
    return <span className="text-muted-foreground">...</span>;
  }

  if (!customer) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="text-muted-foreground truncate" title={customer.name}>
      {customer.name}
    </span>
  );
};

const CategoryCell = ({
  category,
}: {
  category?: { id: string; name?: string } | string | null;
}) => {
  const categoryId = category ? (typeof category === 'object' ? category.id : category) : '';

  const { data: categoryData, isLoading } = useObjectCategory(categoryId, {
    disabled: !categoryId,
  });

  if (!category) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof category === 'object' && category.name) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
        {category.name}
      </span>
    );
  }

  if (isLoading) {
    return <span className="text-muted-foreground">...</span>;
  }

  if (!categoryData) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
      {categoryData.name}
    </span>
  );
};

export function ObjectsOverview({ filterForCurrentUserCustomer = false }: ObjectsOverviewProps) {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = currentUser?.role === 'gmn-admin';

  const tablePreferences = useTablePreferences({
    tableId: filterForCurrentUserCustomer ? 'my-objects-overview' : 'objects-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Get category filter from URL
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const { data: filteredCategory } = useObjectCategory(categoryFilter || '', {
    disabled: !categoryFilter,
  });

  const handleViewDetail = useCallback(
    (id: string) => {
      const route = filterForCurrentUserCustomer
        ? `/c/my-objects/${id}`
        : `/gmn/contract-management/objects/${id}`;
      navigate(route);
    },
    [navigate, filterForCurrentUserCustomer]
  );

  const [objectToDelete, setObjectToDelete] = useState<PortalObject | null>(null);
  const deleteObject = useDeleteObject();

  const handleDelete = async () => {
    if (objectToDelete) {
      try {
        await deleteObject.mutateAsync(objectToDelete.id);
        toast.success(t('objects.objectDeletedSuccessfully'));
        setObjectToDelete(null);
      } catch (_error) {
        toast.error(t('objects.errorDeletingObject'));
      }
    }
  };

  const columns: ColumnDef<PortalObject>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.getValue('name')}</span>
      ),
      size: 250,
      minSize: 150,
      maxSize: 350,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <SortableHeader column={column} title={t('common.description')} />,
      cell: ({ row }) => (
        <span
          className="text-muted-foreground truncate max-w-[400px]"
          title={row.getValue('description')}
        >
          {row.getValue('description') || '-'}
        </span>
      ),
      size: 400,
      minSize: 250,
      maxSize: 600,
    },
    // Category column - only for GMN users
    ...(!filterForCurrentUserCustomer
      ? [
          {
            id: 'category',
            accessorKey: 'category.name',
            header: ({ column }: { column: Column<PortalObject, unknown> }) => (
              <SortableHeader column={column} title={t('objectCategories.category')} />
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const cat = row.original.category;
              return <CategoryCell category={cat} />;
            },
            size: 150,
            minSize: 100,
            maxSize: 200,
          },
        ]
      : []),
    // Only show assigned customer column for GMN users
    ...(!filterForCurrentUserCustomer
      ? [
          {
            id: 'assignedCustomer',
            accessorKey: 'assignedCustomer.name',
            header: ({ column }: { column: Column<PortalObject, unknown> }) => (
              <SortableHeader column={column} title={t('customers.assignedCustomer')} />
            ),
            cell: ({ row }: { row: Row<PortalObject> }) => {
              const assigned = row.original.assignedCustomer;
              return <CustomerCell assignedCustomer={assigned} />;
            },
            size: 200,
            minSize: 150,
            maxSize: 300,
          },
        ]
      : []),
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          object={row.original}
          onDelete={setObjectToDelete}
          onViewDetail={handleViewDetail}
          filterForCurrentUserCustomer={filterForCurrentUserCustomer}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  // Build where clause for filtering
  const whereClause: Record<string, any> = {};
  if (debouncedSearchTerm) {
    whereClause.name = { like: debouncedSearchTerm };
  }

  // Filter by category if specified in URL
  if (categoryFilter) {
    whereClause.category = { equals: categoryFilter };
  }

  if (
    filterForCurrentUserCustomer &&
    currentUser?.role === 'customer-admin' &&
    currentUser.customer
  ) {
    const customerId =
      typeof currentUser.customer === 'object'
        ? currentUser.customer.id
        : (currentUser.customer as string);
    if (customerId) {
      whereClause['assignedCustomer.id'] = { equals: customerId };
    }
  }

  const { data: objectsData, isLoading } = useObjects({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where: Object.keys(whereClause).length > 0 ? whereClause : undefined,
    sort: getSortParam<PortalObject>(sorting),
  });

  const objects = objectsData?.docs || [];
  const totalPages = objectsData?.totalPages || 0;
  const totalDocs = objectsData?.totalDocs || 0;

  const table = useReactTable({
    data: objects,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  const showCustomerEmptyState =
    filterForCurrentUserCustomer && !isLoading && (objectsData?.totalDocs ?? 0) === 0;
  const showObjectsTable = !showCustomerEmptyState;

  // Determine title and breadcrumb
  const pageTitle = filterForCurrentUserCustomer
    ? t('objects.myObjects')
    : filteredCategory
      ? `${t('objects.objects')}: ${filteredCategory.name}`
      : t('objects.manageObjects');

  const breadcrumbTitle = filterForCurrentUserCustomer
    ? t('objects.myObjects')
    : filteredCategory
      ? filteredCategory.name
      : t('navigation.items.allObjects');

  const pageIcon = filteredCategory ? getObjectCategoryIcon(filteredCategory.icon) : Box;
  const pageIconKey = filteredCategory
    ? normalizeObjectCategoryIconKey(filteredCategory.icon)
    : null;

  return (
    <PageContainer>
      <PageTitle
        icon={pageIcon}
        iconTestId={filteredCategory ? 'objects-category-icon' : undefined}
        iconKey={pageIconKey ?? undefined}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('objects.objects')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{breadcrumbTitle}</SimpleBreadcrumbItem>
          </>
        }
        title={pageTitle}
        primaryAction={
          !filterForCurrentUserCustomer
            ? {
                label: t('objects.createObject'),
                onClick: () => navigate('/gmn/contract-management/objects/new'),
                icon: <Plus size={16} />,
                disabled: !isGmnAdmin,
              }
            : undefined
        }
      />

      {showObjectsTable ? (
        <TableCard>
          <TableCardHeader>
            <TableSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t('objects.searchPlaceholder')}
            />
          </TableCardHeader>

          <TableCardContent>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableHeaderRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <TableHead
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                        className={
                          index === 0
                            ? 'pl-6'
                            : index === headerGroup.headers.length - 1
                              ? 'pr-6 text-right'
                              : ''
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableHeaderRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="cursor-pointer"
                      onClick={() => handleViewDetail(row.original.id)}
                    >
                      {row.getVisibleCells().map((cell, index) => (
                        <TableCell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}px` }}
                          className={
                            index === 0
                              ? 'pl-6'
                              : index === row.getVisibleCells().length - 1
                                ? 'pr-6'
                                : ''
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : !isLoading ? (
                  <EmptyTableRow colSpan={columns.length} />
                ) : (
                  <TableLoadingCell colSpan={columns.length} />
                )}
              </TableBody>
            </Table>
          </TableCardContent>

          <TableCardFooter>
            <TablePagination table={table} />
          </TableCardFooter>
        </TableCard>
      ) : (
        <div className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <PackageSearch className="size-5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('objects.myObjectsEmptyTitle')}</p>
              <p className="text-sm text-muted-foreground">
                {t('objects.myObjectsEmptyDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      {!filterForCurrentUserCustomer && (
        <AlertDialog
          open={!!objectToDelete}
          onOpenChange={(open) => !open && setObjectToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('objects.deleteObject')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('objects.deleteObjectConfirmation', {
                  name: objectToDelete?.name || t('objects.object'),
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleteObject.isPending}>
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </PageContainer>
  );
}
